import { type FC, type ReactNode } from "react";

import { EvaluationTypeEnum, RecordReviewDisplayState } from "@app_schema";

import { CompletedRecordsCard } from "@styled/completed_records_card";
import { ProcessingStatsCard } from "@styled/processing_stats_card";

import { EvalTypeBadge } from "./eval_type_badge";
import type { ProcessRecordsStateContext } from "./process_records_context";
import {
  FollowUpFilePillList,
  ProjectFilePillList,
} from "./records_file_pills";
import {
  RecordsStatusBar,
  RsbCheckIcon,
  RsbErrorIcon,
} from "./records_status_bar";
import { SourceFileList } from "./source_file_list";
import { useProcessingCardCollapse } from "@application/contexts/processing_card_collapse_context";

/**
 * Terminal-state RSB renderings — the four displayStates where the Records
 * stage has finished (success or failure). Extracted from
 * `ProcessRecordsStateView` to keep that file under the CLAUDE.md 200-line
 * ceiling. Receives the same context object so the parent's data-assembly
 * remains the single source of truth.
 */
export const CompletedRecordsView: FC<{
  context: ProcessRecordsStateContext;
}> = ({ context }) => {
  const {
    id,
    projectID,
    displayState,
    variant,
    additionalRecordsCount,
    fileSummary,
    evaluationType,
    sourceFiles,
    recordReview,
    newLetterCount,
    reviewedLetterCount,
    filteredLetterCount,
    baselineLetterCount,
    totalPageCount,
    pageBuckets,
    fullPageCount,
    condensedPageCount,
    condensedMultiplier,
    nonBillableUnits,
    netBillableUnits,
    condensedBillableUnits,
    hasCondensedPages,
    addRecordsButton,
  } = context;

  const evalType = evaluationType ?? EvaluationTypeEnum.Comprehensive;

  // Present only when this card is the embedded, expandable processing card
  // (provider set by DashboardRunReportReview); collapses it from its own ✕.
  const onCollapse = useProcessingCardCollapse();

  // Records-processed card surfaces the BillableSection headline only —
  // bucket bars are billing chrome and live on the Billing By Project
  // drill-in instead (Design QA correction 6). `buckets` /
  // `billableUnitsBuckets` are intentionally omitted here so
  // BillableSection skips PageBucketBars. All composition values come
  // pre-computed from `project.recordReview*` (CLAUDE.md "no business
  // logic in the frontend"). Renders on every project type — the
  // `hasCondensedPages` flag inside the data drives whether the composition
  // subline is shown.
  const billableData = {
    totalPages: totalPageCount,
    totalBillablePages: netBillableUnits,
    nonBillableUnits,
    fullPageCount,
    condensedPageCount,
    condensedBillableUnits,
    condensedMultiplier,
    hasCondensedPages,
  };

  const statsCard = (
    <ProcessingStatsCard
      primarySection="letters"
      letters={{
        counts: {
          new: newLetterCount,
          previouslyReviewed: reviewedLetterCount,
          nonEssential: filteredLetterCount,
          priorEval: baselineLetterCount,
        },
      }}
      pages={{
        counts: { total: totalPageCount, ...pageBuckets },
        display: "stat-row",
        heading: "Pages processed",
      }}
      billable={{ data: billableData, variant: "compact" }}
    />
  );

  // A case where a record never finished is not "Records processed" — the
  // clinician is missing letters. Pure switch on the server-computed display
  // state; the count-bearing copy comes from `recordReview.title/subtitle`.
  const hasFailedRecord =
    displayState === RecordReviewDisplayState.CompleteWithFailedRecord;

  const renderCompleted = (
    badge: ReactNode,
    fileList: ReactNode,
  ): ReactNode => (
    <CompletedRecordsCard
      id={id}
      icon={hasFailedRecord ? <RsbErrorIcon /> : <RsbCheckIcon />}
      tone={hasFailedRecord ? "attention" : "success"}
      title={hasFailedRecord ? recordReview?.title : "Records processed"}
      subtitle={hasFailedRecord ? recordReview?.subtitle : fileSummary}
      badge={badge}
      actions={addRecordsButton}
      statsBody={statsCard}
      fileList={fileList}
      onClose={onCollapse ?? undefined}
    />
  );

  switch (displayState) {
    case RecordReviewDisplayState.Complete:
    case RecordReviewDisplayState.CompleteWithFailedRecord:
      return renderCompleted(
        <EvalTypeBadge
          type={evalType}
          counter={additionalRecordsCount}
          tone={
            displayState === RecordReviewDisplayState.CompleteWithFailedRecord
              ? "error"
              : "default"
          }
        />,
        evalType === EvaluationTypeEnum.Followup ? (
          <FollowUpFilePillList
            projectID={projectID}
            priorReviewDoc={recordReview?.priorReviewDocument ?? null}
            baselineSourceDocs={recordReview?.baselineSourceDocuments ?? []}
            zone2Docs={recordReview?.additionalSourceDocuments ?? []}
          />
        ) : (
          <ProjectFilePillList
            projectID={projectID}
            sourceDocs={recordReview?.additionalSourceDocuments ?? []}
          />
        ),
      );

    case RecordReviewDisplayState.CompleteWithFollowUp:
      return renderCompleted(
        <EvalTypeBadge type={evalType} />,
        <FollowUpFilePillList
          projectID={projectID}
          priorReviewDoc={recordReview?.priorReviewDocument ?? null}
          baselineSourceDocs={recordReview?.baselineSourceDocuments ?? []}
          zone2Docs={recordReview?.additionalSourceDocuments ?? []}
        />,
      );

    case RecordReviewDisplayState.Failed:
      return (
        <RecordsStatusBar
          id={id}
          variant={variant}
          icon={<RsbErrorIcon />}
          title={recordReview?.title}
          subtitle={recordReview?.subtitle}
          badgeStack={<EvalTypeBadge type={evalType} />}
          body={<SourceFileList files={sourceFiles} projectID={projectID} />}
        />
      );

    default:
      return null;
  }
};
